import React from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Sparkles, Zap } from "lucide-react";
import { useLanguage } from "../../i18n";

const ApplicationsSection = () => {
  const { language } = useLanguage();
  
  const applications = [
    {
      icon: Zap,
      title: language === 'es' ? "Activaciones de Marca" : "Brand Activations",
      description: language === 'es' 
        ? "Experiencias diseñadas para engagement, impacto viral y conexión emocional con tu audiencia."
        : "Experiences designed for engagement, viral impact and emotional connection with your audience.",
      color: "from-cyan-500 to-blue-600",
      link: "/activaciones-de-marca",
      image: "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png"
    },
    {
      icon: Building2,
      title: language === 'es' ? "Lanzamientos Inmobiliarios" : "Real Estate Launches",
      description: language === 'es' 
        ? "Inauguraciones que posicionan desarrollos como únicos, generando expectativa y acelerando ventas."
        : "Inaugurations that position developments as unique, generating anticipation and accelerating sales.",
      color: "from-blue-500 to-purple-600",
      link: "/lanzamientos-inmobiliarios",
      image: "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lu5h35al_8E4176D1-0298-41D1-A1FE-FEC0AA134D5B.png"
    },
    {
      icon: Users,
      title: language === 'es' ? "Eventos Masivos, Turismo y Gobierno" : "Massive Events, Tourism & Government",
      description: language === 'es'
        ? "Festivales, celebraciones públicas, atracciones turísticas y eventos gubernamentales de gran escala."
        : "Festivals, public celebrations, tourist attractions and large-scale government events.",
      color: "from-purple-500 to-pink-600",
      link: "/eventos-masivos",
      image: "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xy9crazs_IMG_0678.jpeg"
    },
    {
      icon: Sparkles,
      title: language === 'es' ? "Eventos Privados Premium" : "Premium Private Events",
      description: language === 'es'
        ? "Bodas, celebraciones exclusivas y eventos VIP con experiencias de clase mundial."
        : "Weddings, exclusive celebrations and VIP events with world-class experiences.",
      color: "from-pink-500 to-orange-500",
      link: "/eventos-privados",
      image: "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/wjkmdoh0_394EB39E-BAE8-427B-8173-080A499032F7.png"
    },
  ];

  return (
    <section id="aplicaciones" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="applications-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'es' ? 'Experiencias para' : 'Experiences for'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              {language === 'es' ? 'Cada Ocasión' : 'Every Occasion'}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Nuestras experiencias inmersivas se adaptan a cualquier tipo de evento'
              : 'Our immersive experiences adapt to any type of event'}
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {applications.map((app, index) => (
            <Link 
              key={index}
              to={app.link}
              className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-cyan-500/30 transition-all block"
              data-testid={`application-${index}`}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img 
                  src={app.image} 
                  alt={app.title}
                  className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
              </div>
              
              {/* Content */}
              <div className="relative p-6 min-h-[280px] flex flex-col justify-end">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <app.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{app.title}</h3>
                <p className="text-gray-300 leading-relaxed text-sm line-clamp-3">{app.description}</p>
                <span className="inline-flex items-center gap-2 text-cyan-400 text-sm mt-3 group-hover:translate-x-1 transition-transform">
                  {language === 'es' ? 'Ver más →' : 'Learn more →'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ApplicationsSection;
